// @flow
import React, { type Node } from 'react';
import * as StringUtil from 'app/common/lib/StringUtil';
import i18n from 'app/common/lib/i18n';
import Testable from 'app/common/react/ui/Testable';
import Text from 'app/common/react/components/common/ExperimentalLegoGestalt/Text';
import { type CloseupViewType } from 'app/common/react/components/closeup/types';
import { Box, IconButton, Touchable } from 'gestalt';

type Props = {|
  commentCount: number,
  flyout?: Node,
  isLikedByMe: boolean,
  onLikeClick: ({ event: SyntheticMouseEvent<> }) => void,
  onLikeCountClick: ({
    event: SyntheticMouseEvent<HTMLDivElement> | SyntheticKeyboardEvent<HTMLDivElement>,
  }) => void,
  onReplyClick: Function, // TODO(danay):make this more specific after we get rid of old board activity styles
  onReplyCountClick?: () => void,
  hideReplyCount?: boolean,
  reactionCounts: Object,
  small?: boolean,
  variant: CloseupViewType,
|};

export default function Reactions(props: Props) {
  const {
    commentCount = 0,
    reactionCounts = {},
    hideReplyCount,
    isLikedByMe = false,
    onLikeClick,
    onLikeCountClick,
    onReplyClick,
    onReplyCountClick,
    flyout,
    small,
    variant,
  } = props;
  const likeCount = reactionCounts[PCONST.thrift.ReactionType.LIKE] || 0;
  const likeCountText =
    !!likeCount &&
    StringUtil.safeFormat(i18n.ngettext('{{likeCount}} like', '{{likeCount}} likes', likeCount), {
      likeCount,
    });
  const replyCountText =
    !!commentCount &&
    StringUtil.safeFormat(
      i18n.ngettext('{{commentCount}} reply', '{{commentCount}} replies', commentCount),
      { commentCount },
    );

  const iconPaddingX = small ? 1 : 2;
  const showDefaultStyles = variant === 'default';

  return (
    <Box display="flex" alignItems="center">
      {/* Icons and flyout */}
      <Box display="flex" marginStart={-iconPaddingX} marginEnd={-iconPaddingX}>
        <Box paddingX={iconPaddingX}>
          <Testable id="reactions-like">
            <IconButton
              accessibilityLabel="Like"
              bgColor="transparent"
              icon="heart"
              iconColor={isLikedByMe ? 'red' : 'gray'}
              onClick={onLikeClick}
              size={small ? 'sm' : 'md'}
            />
          </Testable>
        </Box>
        <Box paddingX={iconPaddingX}>
          <Testable id="reactions-reply">
            <IconButton
              accessibilityLabel="Reply"
              bgColor="transparent"
              icon="speech"
              iconColor="gray"
              onClick={onReplyClick}
              size={small ? 'sm' : 'md'}
            />
          </Testable>
        </Box>
        {!!flyout && <Box paddingX={iconPaddingX}>{flyout}</Box>}
      </Box>
      {/* Like and comment count */}
      <Box display="flex" flex="grow" justifyContent="end" marginStart={-3} marginEnd={-3}>
        {!!likeCountText && (
          <Testable id="reactions-like-count">
            <Box paddingX={3}>
              <Touchable shape="rounded" onTouch={onLikeCountClick}>
                <Text size="sm" color="gray" weight={showDefaultStyles ? 'semibold' : 'bold'}>
                  {likeCountText}
                </Text>
              </Touchable>
            </Box>
          </Testable>
        )}
        {!!replyCountText &&
          !hideReplyCount && (
            <Testable id="reactions-reply-count">
              <Box paddingX={3}>
                {onReplyCountClick ? (
                  <Touchable shape="rounded" onTouch={onReplyCountClick}>
                    <Text size="sm" color="gray" weight={showDefaultStyles ? 'semibold' : 'bold'}>
                      {replyCountText}
                    </Text>
                  </Touchable>
                ) : (
                  <Text size="sm" color="gray" weight={showDefaultStyles ? 'semibold' : 'bold'}>
                    {replyCountText}
                  </Text>
                )}
              </Box>
            </Testable>
          )}
      </Box>
    </Box>
  );
}
